import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import { DateField } from './DatePicker.jsx'
import { addDays, formatShort } from '../lib/dates.js'
import { useEscapeKey } from '../lib/useEscapeKey.js'

const POP_WIDTH = 340
const POP_PAD = 11
const POP_WIDTH_NOTE = 620
// The most days one save may cover — the same ceiling the server holds (repos/assets.js).
const MAX_DAYS = 62
// Three. The list is a shortcut, not a directory — if what you want is not in the first
// three, another keystroke narrows it faster than reading a longer list does, and a short
// list never scrolls or pushes the rest of the popover off screen.
const MAX_SUGGESTIONS = 3

/**
 * Who has an asset on a day, as a popover anchored to the cell you clicked.
 *
 * One box, not a wall of pills. The question is nearly always "which of our people", but the
 * honest answer is sometimes a shop or a subcontractor, and a list of buttons could only ever
 * offer the first kind — the second needed its own mode to switch into first. So it is a
 * single field: type, and the people you might mean appear underneath. Pick one and it is
 * theirs; type something that is nobody here and it is taken at its word.
 *
 * That is the rule for leaving, too. Enter or a click outside with nothing picked keeps what
 * you typed as an outside party, rather than discarding it and making you find the mode you
 * were supposed to have chosen first. Nothing typed at all is a cancel, because it is.
 */
export default function AssetCellEditor({
  asset,
  date,
  entry,
  staff,
  onBoardIds,
  unitName = 'asset',
  anchor,
  weekStart = 1,
  today,
  onSave,
  onClear,
  onCancel,
}) {
  const staffById = useMemo(() => new Map((staff || []).map((s) => [s.id, s])), [staff])

  // What the box starts with. A day exception opens on its own answer — the person's name, or
  // the outside party as typed. A standing operator opens on THEIR name: it is who has it, and
  // the dirty check below means simply looking and clicking away writes nothing.
  const initialPicked = entry?.staffId || ''
  const initialText =
    entry?.source === 'day' && !entry.staffId
      ? entry.label || ''
      : staffById.get(initialPicked)?.name || ''

  const [text, setText] = useState(initialText)
  const [picked, setPicked] = useState(initialPicked)
  const [internalNote, setInternalNote] = useState(entry?.internalNote || '')
  const [noteOpen, setNoteOpen] = useState(Boolean(entry?.internalNote))
  // Which suggestion the keyboard is on. -1 is "none", deliberately: a plain Enter must mean
  // "take what I typed", not "take whatever happened to be first in the list".
  const [active, setActive] = useState(-1)
  const [listOpen, setListOpen] = useState(false)
  // The last day this decision covers. Left on the day itself it is one save for one day;
  // pushed to Friday it hands a floater over for the rest of the week in one go.
  const [through, setThrough] = useState(date)

  const width = noteOpen ? POP_WIDTH_NOTE : POP_WIDTH
  const popRef = useRef(null)
  // Below the cell, or above it — decided ONCE, when the popover opens, and then kept.
  // Its height changes as you type (the suggestion list appears, a warning shows), and
  // re-deciding on every keystroke made it flip sides under the pointer.
  const sideRef = useRef(null)
  const inputRef = useRef(null)
  const [pos, setPos] = useState(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  /**
   * Where the popover sits. Placed below the cell when there is room and above it when there
   * is not, and never off the side.
   *
   * The left edge is worked out from the CLOSED width and then held. Opening the note widens
   * the popover, and re-deriving the left from the new width pulled it back across the screen
   * — so a panel that is supposed to unfold to the right grew in both directions at once and
   * the field you were typing in slid out from under the pointer. It gives way only when the
   * wider popover would genuinely run off the right edge, which is the one case where staying
   * put is not an option.
   */
  /**
   * Where the popover sits — worked out when it OPENS, and then left alone.
   *
   * It used to be recomputed on every keystroke, and its height changes as you type: the
   * suggestion list appears, a hint shows, the note unfolds. Each of those re-derived the top
   * from the new height, so the whole dialog hopped up and down under the pointer while
   * somebody was typing into it — measured at 759, then 644, then 730 across three
   * keystrokes.
   *
   * So: placed once, below the cell when there is room and above it when there is not, and
   * never off the side. If it later grows taller than the room beneath it, it scrolls inside
   * itself rather than moving — a dialog that stays put and scrolls is far easier to use than
   * one that keeps its whole content visible by jumping.
   *
   * `width` is the one thing that repositions it, because opening the note has to be allowed
   * to pull it left if it would otherwise run off the right edge. The left is derived from the
   * CLOSED width and held, so the note unfolds rightward rather than growing both ways.
   */
  useLayoutEffect(() => {
    const el = popRef.current
    if (!el || !anchor) return
    const vw = window.innerWidth
    const vh = window.innerHeight
    if (!sideRef.current) {
      sideRef.current = anchor.bottom + 8 + el.offsetHeight <= vh - 8 ? 'below' : 'above'
    }
    const top =
      sideRef.current === 'below'
        ? anchor.bottom + 8
        : Math.max(8, anchor.top - el.offsetHeight - 8)
    const anchored = Math.max(8, Math.min(anchor.left, vw - POP_WIDTH - 8))
    const left = Math.max(8, Math.min(anchored, vw - width - 8))
    setPos({ top, left, maxHeight: Math.max(160, vh - top - 8) })
  }, [anchor, width])

  // The people you might mean, those already on the board that day first — that is who is
  // about to drive somewhere. Filtered by what has been typed, and never shown once a person
  // is picked, since the question is answered.
  //
  // That fact orders the list and nothing more. It used to label the others "not on the
  // board", which read as a warning but was not one: having no job booked yet is no reason
  // to hesitate before handing somebody a van, and the label said nothing about the thing
  // that would be — whether they are away. Better silent than misleadingly cautionary.
  const matches = useMemo(() => {
    if (picked) return []
    const q = text.trim().toLowerCase()
    return [...(staff || [])]
      .filter((s) => !q || s.name.toLowerCase().includes(q))
      .sort((a, b) => {
        const ao = onBoardIds?.has(a.id) ? 0 : 1
        const bo = onBoardIds?.has(b.id) ? 0 : 1
        return ao - bo || a.name.localeCompare(b.name)
      })
      .slice(0, MAX_SUGGESTIONS)
  }, [staff, onBoardIds, text, picked])

  const showList = listOpen && !picked && matches.length > 0

  /**
   * Typing somebody's name in full and pressing Enter means that person, not an outside party
   * who happens to share their name. Without this the one gesture most likely to be used —
   * type the name, hit Enter — produced a custom label that merely looked right, and the board
   * would not have linked it to them at all.
   */
  const exactMatch = useMemo(() => {
    const q = text.trim().toLowerCase()
    if (!q) return null
    return (staff || []).find((s) => s.name.toLowerCase() === q) || null
  }, [staff, text])

  const resolved = picked
    ? { staffUserId: picked, label: null }
    : exactMatch
      ? { staffUserId: exactMatch.id, label: null }
      : text.trim()
        ? { staffUserId: null, label: text.trim() }
        : null

  // Nothing changed means nothing to write — otherwise opening a cell to look at it would
  // spend a request, an audit entry and a version bump restating what it already said.
  const dirty =
    (resolved?.staffUserId || '') !== initialPicked ||
    (resolved?.label || '') !== (entry?.source === 'day' && !entry.staffId ? entry.label || '' : '') ||
    internalNote.trim() !== (entry?.internalNote || '') ||
    through !== date

  const dates = useMemo(() => {
    const out = [date]
    let d = date
    while (d < through && out.length < MAX_DAYS) {
      d = addDays(d, 1)
      out.push(d)
    }
    return out
  }, [date, through])

  function save() {
    if (!resolved) return onCancel()
    if (!dirty) return onCancel()
    onSave({
      dates,
      staffUserId: resolved.staffUserId,
      label: resolved.label,
      // Always sent, so emptying the box clears the note.
      internalNote: internalNote.trim(),
    })
  }

  const choose = (s) => {
    setPicked(s.id)
    setText(s.name)
    setListOpen(false)
    setActive(-1)
    inputRef.current?.focus()
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setListOpen(true)
      setActive((i) => Math.min(i + 1, matches.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, -1))
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      // Only an arrowed-to suggestion counts as picking from the list. Everything else is
      // "what I typed", which is the whole point of the field.
      if (showList && active >= 0) return choose(matches[active])
      save()
    }
  }

  // Escape abandons; a click outside keeps. Two different intentions, and only one of them
  // should be guessed at — see the commit rule at the top of this file.
  useEscapeKey(onCancel)

  const standing = entry?.source === 'standing' ? staffById.get(entry.staffId) : null

  return (
    <div
      className="cell-pop-backdrop"
      onClick={save}
      onContextMenu={(e) => { e.preventDefault(); save() }}
    >
      <form
        ref={popRef}
        className={`cell-pop ${noteOpen ? 'with-note' : ''}`}
        style={{
          width,
          '--pop-main-w': `${POP_WIDTH - POP_PAD * 2}px`,
          ...(pos || { top: -9999, left: -9999 }),
        }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); save() }}
      >
        {/* No asset name and no date. Both are read off the row and column the popover is
            attached to, and repeating them here spent the top of a small dialog saying what
            the thing it is pointing at already says. */}
        <div className="cell-pop-head bare">
          <button
            type="button"
            className={`cell-pop-note-btn ${noteOpen ? 'on' : ''}`}
            onClick={() => setNoteOpen((v) => !v)}
            aria-pressed={noteOpen}
            title={noteOpen ? 'Hide the internal note' : 'A private note for whoever is running the board'}
          >
            <Icon name="note" size={13} strokeWidth={2.2} />
            Internal Note
            {!noteOpen && internalNote.trim() && <span className="cell-pop-note-dot" />}
          </button>
          <button type="button" className="cell-pop-x" onClick={onCancel} aria-label="Close">
            <Icon name="close" size={14} strokeWidth={2.2} />
          </button>
        </div>

        <div className="cell-pop-body">
          <div className="cell-pop-main">
            <div className="ac">
              <input
                ref={inputRef}
                className="cell-pop-input"
                type="text"
                maxLength={120}
                value={text}
                placeholder={`Who has this ${unitName}?`}
                autoComplete="off"
                role="combobox"
                aria-expanded={showList}
                aria-autocomplete="list"
                onChange={(e) => {
                  setText(e.target.value)
                  // Editing the text un-picks: the name in the box is no longer the person's.
                  setPicked('')
                  setActive(-1)
                  setListOpen(true)
                }}
                onFocus={() => setListOpen(true)}
                onKeyDown={onKeyDown}
              />
              {picked && (
                <button
                  type="button"
                  className="ac-clear"
                  aria-label="Clear"
                  onClick={() => { setPicked(''); setText(''); setListOpen(true); inputRef.current?.focus() }}
                >
                  <Icon name="close" size={12} strokeWidth={2.4} />
                </button>
              )}

              {showList && (
                <ul className="ac-list" role="listbox">
                  {matches.map((s, i) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={i === active}
                        className={`ac-option ${i === active ? 'active' : ''}`}
                        // mousedown, not click: the input's blur would otherwise close the
                        // list out from under the pointer before the click landed.
                        onMouseDown={(e) => { e.preventDefault(); choose(s) }}
                        onMouseEnter={() => setActive(i)}
                      >
                        <span className="ac-option-name">{s.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Two wordings, because the two situations are genuinely different. With names
                still listed, what happens on Enter is not settled — one of them might be who
                you mean — so it says "could". With nothing left matching, the decision is
                made, and it says "will". */}
            {!picked && !exactMatch && text.trim() && (
              <div className="cell-pop-hint ac-custom">
                {showList ? 'Could be saved as custom entry' : 'Will be saved as custom entry'}
              </div>
            )}
            {standing && !dirty && (
              <div className="cell-pop-hint">Usually with {standing.name}</div>
            )}

            <div className="cell-pop-section fleet-through">
              <div className="cell-pop-legend">Through</div>
              <DateField
                value={through}
                onChange={setThrough}
                min={date}
                max={addDays(date, MAX_DAYS - 1)}
                weekStart={weekStart}
                today={today}
                withYear={false}
                label="Apply through"
              />
              {dates.length > 1 && (
                <span className="cell-pop-hint">
                  {dates.length} days, {formatShort(date)} to {formatShort(through)}
                </span>
              )}
            </div>
          </div>

          {noteOpen && (
            <div className="cell-pop-note">
              <div className="cell-pop-legend">Internal note</div>
              <textarea
                className="cell-pop-textarea"
                maxLength={2000}
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="cell-pop-foot">
          {entry?.source === 'day' ? (
            <button
              type="button"
              className="btn sm ghost danger"
              onClick={onClear}
              title="Remove this day's entry — the asset goes back to whoever usually has it"
            >
              <Icon name="trash" size={14} />
              Delete
            </button>
          ) : (
            <span className="cell-pop-hint">Enter to save</span>
          )}
          <span className="spacer" />
          <button type="button" className="btn sm" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn sm primary" disabled={!resolved}>
            {dates.length > 1 ? `Save ${dates.length} days` : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}
